import React, { useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronUp, PlusCircle} from 'feather-icons-react/build/IconComponents';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import AddCategoryList from '../../core/modals/inventory/addcategorylist';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Table from '../../core/pagination/datatable';
import { useToast } from '../../Toaster/ToastContext';
import Loader from '../loader/loader';
import { apiClient } from '../../services/apiClient';

const CategoryList = () => {

    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const { showToast } = useToast();
    const hasFetched = useRef(false);
    const [permissions, setPermissions] = useState({
        create: false,
        read: false,
        update: false,
        delete: false,
    });

    useEffect(() => {
        const storedPermissions = localStorage.getItem("rolespermissions");
        let parsedPermissions = [];
        try {
            const Permissions = JSON.parse(storedPermissions);
            const getPermissions = JSON.parse(Permissions.permissions)
            if(getPermissions.permissions){
                parsedPermissions = getPermissions.permissions;
            }
            else {
                parsedPermissions = getPermissions;
            }
        
            const inventoryPermissions = parsedPermissions.find(
                (item) => item.title === "Inventory"
            );

            const productPermissions = inventoryPermissions?.childPage?.find(
                (child) => child.title === "Category"
            );
            // console.log("search product", productPermissions)

            if (productPermissions) {
                setPermissions({
                    create: productPermissions.create || false,
                    read: productPermissions.read || false,
                    update: productPermissions.update || false,
                    delete: productPermissions.delete || false,
                });
            } else {
                console.error("Product permissions not found in localStorage");
                showToast("Product permissions not found.", "error", "Permissions Error");
            }
        } catch (error) {
            console.error("Error parsing permissions from localStorage:", error);
            showToast("Failed to load permissions.", "error", "Permissions Error");
        }
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        hasFetched.current = false;
        try {
            const response = await apiClient.post('/paginate-category', {
                pageIndex: 0,
                pageSize: 50,
                previousPageIndex: 0,
            });
            const data = await response;
            if (data.success) {
                setCategories(data.data);
                setFilteredCategories(data.data);
            } else {
                setCategories([]);
                setFilteredCategories([]);
            }
        } catch (error) {
            showToast(error.message || 'Failed to fetch categories', 'error', 'Categories');
        } finally {
            setLoading(false);
            hasFetched.current = true;
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleAddCategory = () => {
        setCurrentCategory(null);
    };

    const handleEditCategory = (category) => {
        setCurrentCategory(category);
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setFilteredCategories(
            categories.filter((category) =>
                category.name.toLowerCase().includes(term) ||
                category.slug.toLowerCase().includes(term) ||
                category.status.toLowerCase().includes(term)
            )
        );
    };

    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    );

    const columns = [
        {
            title: 'Category',
            dataIndex: 'name',
            sorter: (a, b) => a.name.length - b.name.length,
        },
        {
            title: 'Category Slug',
            dataIndex: 'slug',
            sorter: (a, b) => a.slug.length - b.slug.length,
        },
        {
            title: 'Created On',
            dataIndex: 'created_at',
            sorter: (a, b) => a.created_at.length - b.created_at.length,
            render: (text) => {
                const date = new Date(text);
                return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).split('/').join('-');
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (text) => (
                <span className={`badge ${text === 'active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                    {text === 'active' ? 'Active' : 'Inactive'}
                </span>
            ),
            sorter: (a, b) => a.status.length - b.status.length,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (_, record) => (
                <td className="action-table-data">
                    <div className="edit-delete-action">
                        {permissions.read && (
                            <Link
                                className="me-2 p-2"
                                to="#"
                                onClick={() => handleEditCategory(record)}
                                data-bs-toggle="modal"
                                data-bs-target="#add-category"
                            >
                                <i data-feather="edit" className="feather-edit"></i>
                            </Link>
                        )}
                        {permissions.delete && (
                            <Link
                                className="confirm-text p-2"
                                to="#"
                                onClick={() => showConfirmationAlert(record)}
                            >
                                <i data-feather="trash-2" className="feather-trash-2"></i>
                            </Link>
                        )}
                    </div>
                </td>
            ),
        },
    ];

    const MySwal = withReactContent(Swal);

    const showConfirmationAlert = (record) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            showCancelButton: true,
            confirmButtonColor: '#00ff00',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#ff0000',
            cancelButtonText: 'Cancel',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    const response = await apiClient.post('/delete-category', {
                        id: record.id,
                    })
                    const data = await response;
                    if (data.success) {
                        showToast('Category deleted successfully', 'success', 'Category Operation!');
                        fetchCategories();
                    } else {
                        showToast(data.message || 'Failed to delete category', 'error', 'Category Operation!');
                    }
                } catch (error) {
                    showToast(error.message || 'Failed to delete category', 'error', 'Category Operation!');
                } finally {
                    setLoading(false);
                }
            } else {
                MySwal.close();
            }
        });
    };

    return (
        <div>
            <Loader loading={loading} />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Category</h4>
                                <h6>Manage your categories</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? 'active' : ''}
                                        onClick={() => {
                                            dispatch(setToogleHeader(!data));
                                        }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        <div className="page-btn">
                            {permissions.create && (
                                <Link
                                    to="#"
                                    className="btn btn-added"
                                    onClick={handleAddCategory}
                                    data-bs-toggle="modal"
                                    data-bs-target="#add-category"
                                >
                                    <PlusCircle className="me-2" />
                                    Add New Category
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="card table-list-card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search by Category, Slug, or Status"
                                            className="form-control form-control-sm formsearch"
                                            onChange={handleSearch}
                                        />
                                        <Link to="#" className="btn btn-searchset">
                                            <i data-feather="search" className="feather-search" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={filteredCategories}
                                    rowKey={(record) => record.id}
                                    rowSelection = {null}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AddCategoryList
                category={currentCategory}
                onSave={() => {
                    fetchCategories();
                }}
            />
        </div>
    );
};

export default CategoryList;